import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useStartAction } from '../hooks/useStartAction';
import { ForLawyersHead } from '../components/ForLawyersHead';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 3rem;
  overflow-x: hidden;
  width: 100%;
`;


const Hero = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 9rem 2rem 5rem;
  animation: ${fadeIn} 0.5s ease both;
`;

const Eyebrow = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: var(--brand-text-muted);
  text-transform: lowercase;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.1;
  margin-bottom: 1.25rem;
`;

const Sub = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.15rem);
  color: #666;
  line-height: 1.65;
  max-width: 680px;
  margin-bottom: 2.5rem;
`;

const CTA = styled.button`
  background: var(--brand-black);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.9rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #2a2a2a; }
`;

const Features = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid var(--brand-line);
  border-radius: 12px;
  padding: 1.75rem;
  animation: ${fadeIn} 0.5s ease both;
`;

const CardTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--brand-black);
  margin-bottom: 0.5rem;
`;

const CardBody = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.55;
  margin: 0;
`;

const ForLawyers: React.FC = () => {
  // Signed in: the app. Signed out: /start. One rule, shared with this page's rebuild (src/hooks/useStartAction.ts).
  const handleCTA = useStartAction('/start');

  return (
    <Page>
      {/* One head, shared with this page's rebuild (src/components/ForLawyersHead.tsx). */}
      <ForLawyersHead />


      <Hero>
        <Eyebrow>Your 24/7 AI Associate</Eyebrow>
        <Title>AI for Solo &amp; Boutique Firms</Title>
        <Sub>
          Compete with larger firms by automating your drafting, bilingual intake,
          and routine correspondence. Scale your practice without scaling headcount.
        </Sub>
        <CTA onClick={handleCTA}>Start Your Free Trial</CTA>
      </Hero>

      <Features>
        <Card>
          <CardTitle>Document Drafting</CardTitle>
          <CardBody>Generate demand letters, contracts, and pleadings in minutes. Review and sign off — prest0 handles the first draft.</CardBody>
        </Card>
        <Card>
          <CardTitle>Bilingual Intake</CardTitle>
          <CardBody>Communicate with Spanish-speaking clients in their language. Automatic translation keeps nothing lost in translation.</CardBody>
        </Card>
        <Card>
          <CardTitle>Deep Legal Research</CardTitle>
          <CardBody>Case law synthesis with citations in minutes, not hours. Stay on top of the law without a full research team.</CardBody>
        </Card>
        <Card>
          <CardTitle>Immigration &amp; Workers' Comp</CardTitle>
          <CardBody>Specialized workflows for I-589, WCAB pre-hearing statements, demand letters, and more — built for your practice areas.</CardBody>
        </Card>
      </Features>
    </Page>
  );
};

export default ForLawyers;
